import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Divider,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { OrderLifecycleSettlement } from '@/types'

interface OrderSettlementsSectionProps {
  settlements?: OrderLifecycleSettlement[]
  lifecycleStatus?: string | null
  locale?: string
  t: TFunction
  formatStatus: (status: string | undefined, translate: TFunction) => string
  formatAmount: (value?: string | null) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  renderTxCell: (value: string | null | undefined, chainId?: string | number | null) => JSX.Element | null
  chainId?: string | number | null
}

export const OrderSettlementsSection = ({
  settlements,
  lifecycleStatus,
  t,
  formatStatus,
  formatAmount,
  renderTxCell,
  chainId,
}: OrderSettlementsSectionProps) => {
  if (!settlements || settlements.length === 0) {
    const normalizedStatus = lifecycleStatus?.toLowerCase()
    const isSettledStatus = normalizedStatus
      ? normalizedStatus.includes('settled') || normalizedStatus.includes('settlement')
      : false

    if (isSettledStatus) {
      const statusLabel = formatStatus(lifecycleStatus ?? '', t)
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            {t('orders.lifecycle.settlementsPending', { status: statusLabel })}
          </Text>
        </Alert>
      )
    }

    return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
  }

  return (
    <VStack align="stretch" spacing={4}>
      {settlements.map(settlement => (
        <Box key={settlement.id} borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="medium">
                {t('orders.lifecycle.settlements')} #{settlement.seq ?? 0}
              </Text>
              <Badge colorScheme={settlement.isVerified ? 'green' : 'orange'}>
                {formatStatus(settlement.status, t)}
              </Badge>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
              <Text>
                {t('orders.lifecycle.amounts.total')}: {formatAmount(settlement.amounts?.totalUsd)}
              </Text>
              <Text>
                {t('orders.lifecycle.amounts.merchant')}: {formatAmount(settlement.amounts?.merchantUsd)}
              </Text>
              <Text>
                {t('orders.lifecycle.amounts.platform')}: {formatAmount(settlement.amounts?.platformUsd)}
              </Text>
            </SimpleGrid>
            <Divider />
            <VStack align="stretch" spacing={2}>
              <HStack spacing={3} align="flex-start">
                <Text fontSize="sm" fontWeight="medium">
                  {t('orders.lifecycle.txHash')}
                </Text>
                {renderTxCell(settlement.tx?.hash ?? undefined, chainId)}
              </HStack>
              {Boolean(settlement.tx?.blockNumber ?? settlement.tx?.blockNumber === 0) && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.blockNumber')}
                  </Text>
                  <Text fontSize="sm">
                    {settlement.tx?.blockNumber != null ? String(settlement.tx.blockNumber) : t('common.na')}
                  </Text>
                </HStack>
              )}
              {settlement.tx?.gasUsed && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.gasUsed')}
                  </Text>
                  <Text fontSize="sm">{settlement.tx.gasUsed}</Text>
                </HStack>
              )}
              {settlement.tx?.gasPrice && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.gasPrice')}
                  </Text>
                  <Text fontSize="sm">{settlement.tx.gasPrice}</Text>
                </HStack>
              )}
              {settlement.notes?.settle && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.notes')}
                  </Text>
                  <Text fontSize="sm">{settlement.notes.settle}</Text>
                </HStack>
              )}
              {settlement.notes?.verification && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.initializationNotes')}
                  </Text>
                  <Text fontSize="sm">{settlement.notes.verification}</Text>
                </HStack>
              )}
            </VStack>
          </VStack>
        </Box>
      ))}
    </VStack>
  )
}
